import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule, HttpRequest } from '@angular/common/http';
import localeAt from '@angular/common/locales/en-AT';
import localePl from '@angular/common/locales/pl';
import localEs from '@angular/common/locales/es';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ShellModule } from '@bd/shell/shell.module';
import { ApmErrorHandler, ApmModule, ApmService } from '@elastic/apm-rum-angular';
import { environment } from '@bd-env/environment';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { webSocketAccessTokenFactory } from 'chronos-core-client';
import {
  ApiAuthorizationModule,
  AUTHORIZE_INTERCEPTOR_MATCHER,
  AUTHORIZE_LOGIN_HOOKS,
  AuthorizeAccessTokenFactory,
  AuthorizeInterceptor,
  AuthorizeService,
  ChronosSharedConfiguration,
  ChronosSharedModule,
  FormatQuantityPipe,
  FormatUnitIdPipe,
  HttpErrorInterceptor,
  sameOriginMatcher
} from 'chronos-shared';
import { AppInitService } from './app-init.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { InitService } from './init.service';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', `.json?cache-buster=${new Date().getTime()}`);
}

function initializeApp(appInitService: AppInitService) {
  return (): Promise<any> => appInitService.Init();
}

function initializeWorkCenterOnLogin(workCenterInitService: InitService) {
  return (): Promise<any> => workCenterInitService.initializeWorkCenter();
}

function createAuthInterceptorMatcher(sharedConfiguration: ChronosSharedConfiguration) {
  return (req: HttpRequest<any>) => req.url.startsWith(sharedConfiguration.coreRootUrl);
}

registerLocaleData(localeAt);
registerLocaleData(localePl);
registerLocaleData(localEs);

@NgModule({
  declarations: [AppComponent],
  imports: [
    ApmModule,
    CoreModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      },
      isolate: false
    }),
    ChronosSharedModule.forRoot({
      pandaRootUrl: null,
      coreRootUrl: environment.basedataApiUrl,
      liveRootUrl: null,
      signalrBaseUrl: null,
      basedataRootUrl: environment.basedataApiUrl
    }),
    ShellModule,
    ApiAuthorizationModule.forRoot({
      authority: 'https://portal.chronos.tietoevry.com/authenticator',
      clientId: 'chronos-dev'
    })
  ],
  providers: [
    ApmService,
    { provide: ErrorHandler, useClass: ApmErrorHandler },
    { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [AppInitService], multi: true },
    { provide: LOCALE_ID, useValue: 'en-AT' },
    { provide: HTTP_INTERCEPTORS, useClass: AuthorizeInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: AUTHORIZE_INTERCEPTOR_MATCHER, useValue: sameOriginMatcher, multi: true },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: 'short' } },
    {
      provide: AUTHORIZE_INTERCEPTOR_MATCHER,
      useFactory: createAuthInterceptorMatcher,
      deps: [ChronosSharedConfiguration],
      multi: true
    },
    {
      provide: AUTHORIZE_LOGIN_HOOKS,
      useFactory: initializeWorkCenterOnLogin,
      deps: [InitService],
      multi: true
    },
    { provide: webSocketAccessTokenFactory, useClass: AuthorizeAccessTokenFactory, deps: [AuthorizeService] },
    FormatQuantityPipe,
    FormatUnitIdPipe
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
